import Vue from "vue";
import StoreTrackableInterface from "../../interfaces/storeTrackableInterface";
import UndoRedoHelper from "../../helpers/undoRedoHelper";
import {RelationType} from "../../models/relationType";
import {IPeekerOptions} from "../../services/peeker";
import {Hash} from "../../interfaces/hashInterfaces";
import {IChanges, IDestroy} from "../../interfaces/undoRedoInterfaces"
import {Logger} from "~/services/logger";

interface IChangeSet {
  changes: object & IChanges,
  is_persisted: boolean,
  peekerOptions?: IPeekerOptions,
  area?: number | string
}

export default function <T extends StoreTrackableInterface>(builderModel: T) {
  return {
    state: {
      done: [],
      undone: [],
      helper: {
        mergeJson(...arg: any[]): object {
          if (arguments.length === 0) {
            return {};
          }
          const first = arguments[0];
          for (let i = 1; i < arguments.length; i++) {
            const other = arguments[i];
            const propertyNames = Object.getOwnPropertyNames(other);
            for (let j = 0; j < propertyNames.length; j++) {
              const propertyName = propertyNames[j];
              if (propertyName === "__ob__") continue;
              const property = other[propertyName];
              if (Array.isArray(property)) {
                let toAdd = property;
                if (!first[propertyName]) {
                  first[propertyName] = [];
                } else {
                  toAdd = [];
                  // Merge objects based on matching client id
                  for (const newItem of property) {
                    let found = false;
                    for (const existingItem of first[propertyName]) {
                      if (existingItem.client_id && existingItem.client_id === newItem.client_id) {
                        this.mergeJson(existingItem, newItem);
                        found = true;
                        break;
                      }
                    }
                    if (!found) {
                      toAdd.push(newItem);
                    }
                  }
                }
                /**
                 * @see {formStoreFactoryTrackedMixin.test.ts} array of primitives should take 2nd obj, array of client_id objs should merge together
                 */
                if (toAdd.length === 0 || toAdd.length > 0 && typeof toAdd[0] === 'object' && toAdd[0].client_id) {
                  first[propertyName] = first[propertyName].concat(toAdd);
                } else {
                  first[propertyName] = toAdd
                }
              } else if (property && typeof property === 'object') {
                if (!first[propertyName]) {
                  first[propertyName] = {};
                }
                this.mergeJson(first[propertyName], property);
              } else {
                first[propertyName] = property;
              }
            }
          }
          return first;
        },

        getChangesJsonFromChangeSets(rootModel: T, unpersistedChangeSets: Array<IChangeSet>, forward: boolean = true): object {
          if (unpersistedChangeSets === null || !unpersistedChangeSets.length) {
            return {};
          }
          const changeSetsArray = Array.isArray(unpersistedChangeSets) ? unpersistedChangeSets : [unpersistedChangeSets];
          let json: Hash = {};
          const changesSet: IChangeSet = changeSetsArray[0];
          const changes = changesSet.changes;

          json = this.getChangesJson(rootModel, changes, forward);

          if (changeSetsArray.length > 1) {
            json = this.mergeJson(json, this.getChangesJsonFromChangeSets(rootModel, changeSetsArray.splice(1), forward));
          }

          return json;
        },

        /**
         * Recurses through current changeSet, generating a json object to send to server for update
         *
         * @param currentModel
         * @param changes
         * @param isForward
         */

        getChangesJson(currentModel: T, changes: Hash & IChanges, isForward: boolean = true): Hash {
          const json: any = {};
          /**
           * I'm not exactly sure why sometimes currentModel is empty
           * I was finding it to be empty when undoing a create ProcessFlow IterationStep at the point where this method
           * wants currentModel to be of the key +variable_map+ for the IterationStep
           */
          if (!currentModel) {
            Logger.errorOnce('storeFactoryTrackedMixin#getChangesJson', 'returning early, `currentModel` not found for `changes`! Is your expected model missing from its repo?');
            return json;
          }
          const parentEmbeddedModels = currentModel.EMBEDDED_MODELS;
          const parentEmbeddedModelKeys = Object.keys(parentEmbeddedModels);


          for (const key in changes) {
            if (parentEmbeddedModelKeys.includes(key)) {
              if (parentEmbeddedModels[key].relationType === RelationType.singleObject) {
                let childKeyName;
                if (currentModel.EMBEDDED_HASHES.includes(key)) {
                  childKeyName = key;
                } else {
                  childKeyName = `${key}_attributes`;
                }
                json[childKeyName] = this.getChangesJson(currentModel[key], changes[key], isForward);
              } else {
                json[`${key}_attributes`] = json[`${key}_attributes`] || [];
                for (let i = 0, len = changes[key].length; i < len; i++) {
                  const change = changes[key][i];
                  UndoRedoHelper.pushOrReplace(
                    json[`${key}_attributes`],
                    this.getChangesJson(change._model.repo.find(change.client_id), change, isForward)
                  );
                }
              }
            } else if (['id', 'client_id'].includes(key)) {
              json[key] = changes[key];
            } else if (key === 'is_persisted') {

            } else if (key === '_destroy') {
              UndoRedoHelper.handleDestroy(json, changes, isForward);
            } else {
              if (isForward) {
                json[key] = changes[key].newVal;
              } else {
                json[key] = changes[key].oldVal;
              }
            }
          }

          return json;
        },

        destroyedItemsSorted<TChanges>(destroyedItems: Array<TChanges>, isForward: boolean) : Array<TChanges> {
          return isForward ? destroyedItems.slice() : destroyedItems.slice().reverse();
        },

        loadChangeSet(rootModel: T, changeSet, isForward = false): void {
          const changes = changeSet.changes;
          this.loadProperties(rootModel, changes, isForward);
          rootModel.clearChanges();
          rootModel.queueSendChangesToServer();
        },

        loadProperties(parentModel: T, changes: Hash<Hash & {_destroy: IDestroy}>, isForward = false): void {
          const object = (<any>parentModel).constructor.repo.find(changes.client_id || changes.id);
          if (!object) {
            return;
          }

          const parentEmbeddedModels = parentModel.EMBEDDED_MODELS;
          const parentEmbeddedModelKeys = Object.keys(parentEmbeddedModels);

          for (const key in changes) {
            if (parentEmbeddedModelKeys.includes(key)) {
              if (parentEmbeddedModels[key].relationType === RelationType.singleObject) {
                let change = changes[key]
                /**
                 * Used to find model with parentModel[key] but that will be null when
                 * redoing a creation fo embeds_one type
                 */
                const childModel = parentModel[key] || change._model.repo.find(change.client_id)
                this.loadProperties(childModel, change, isForward);
              } else {
                for (let i = 0, len = changes[key].length; i < len; i++) {
                  const change = changes[key][i];
                  this.loadProperties(change._model.repo.find(change.client_id), change, isForward);
                }
              }
            } else if (key === '_destroy') {
              for (const client_id in changes[key]) {
                const destroyedItemsSorted = this.destroyedItemsSorted(changes._destroy[client_id], isForward);
                for (const destroyObject of destroyedItemsSorted) {
                  const val = isForward ? destroyObject.newVal : destroyObject.oldVal;
                  let parentObj = null;
                  let itemKey = null;
                  if (parentEmbeddedModelKeys.includes(destroyObject.parentRoot)) {
                    if (parentEmbeddedModels[destroyObject.parentRoot].relationType === RelationType.singleObject) {
                      parentObj = object;
                      itemKey = destroyObject.parentRoot;
                    } else {
                      parentObj = object[destroyObject.parentRoot];
                      itemKey = client_id;
                    }
                  }
                  const obj = destroyObject.isReplace ? isForward ? destroyObject.object : destroyObject.oldObject : destroyObject.object
                  if (val) {
                    if (obj.unmarkForDestruction instanceof Function) obj.unmarkForDestruction()
                    Vue.set(parentObj, itemKey, obj.loadFromJson(val));
                  } else {
                    if (obj.markForDestruction instanceof Function) obj.markForDestruction()
                    Vue.delete(parentObj, itemKey);
                  }
                }
              }
            } else if (key !== 'id' && key !== 'client_id') {
              let targetVal = isForward ? changes[key].newVal : changes[key].oldVal;
              // to undo sending "empty" array for rails
              if (Array.isArray(targetVal) && targetVal.length === 1 && targetVal[0] === '') {
                targetVal = [];
              }
              object[key] = targetVal;
            }
          }
        }
      },
    },
    mutations: {
      saveChanges(state, {changes, area, peekerOptions} ) {
        state.done.push({changes, area, peekerOptions, is_persisted: false});
        state.undone = [];
      },
      undo(state) {
        window.CapybaraLockstep?.startWork('undo')
        builderModel.saveChangesToStore();
        if (state.done.length > 0) {
          const peekChangeSet = state.done[state.done.length - 1];
          if (state.peeker) {
            if (peekChangeSet.peekerOptions && state.router && peekChangeSet.peekerOptions.area !== state.router.currentRoute.fullPath) {
              state.peeker.peek(peekChangeSet.peekerOptions);
              return;
            }
          }
          const changeSet = state.done.pop();
          changeSet.is_persisted = !changeSet.is_persisted;
          state.undone.push(changeSet);
          state.helper.loadChangeSet(builderModel, changeSet, false);
        }
        window.CapybaraLockstep?.stopWork('undo')
      },
      redo(state) {
        window.CapybaraLockstep?.startWork('redo')
        if (state.undone.length > 0) {
          const peekChangeSet = state.undone[state.undone.length - 1];
          if (state.peeker) {
            if (peekChangeSet.peekerOptions && state.router && peekChangeSet.peekerOptions.area !== state.router.currentRoute.fullPath) {
              state.peeker.peek(peekChangeSet.peekerOptions);
              return;
            }
          }
          const changeSet = state.undone.pop();
          changeSet.is_persisted = !changeSet.is_persisted;
          state.done.push(changeSet);
          state.helper.loadChangeSet(builderModel, changeSet, true);
        }
        window.CapybaraLockstep?.stopWork('redo')
      },
      clearUndoRedo(state) {
        state.done = [];
        state.undone = [];
      },
      persistChanges(state, { changes }) {
        changes.forEach((change) => {
          change.is_persisted = true;
        });
      },
      updateProperties(state, {model, json, props}) {
        props.forEach(prop => model[prop] = json[prop]);
      },
    },
    getters: {
      unpersistedChangeSets: (state) => {
        return state.done.filter((change) => !change.is_persisted).concat(state.undone.filter((change) => !change.is_persisted));
      },
      unpersistedDoneChangeSets: (state) => {
        return state.done.filter((change) => !change.is_persisted);
      },
      unpersistedUndoneChangeSets: (state) => {
        return state.undone.filter((change) => !change.is_persisted);
      },
      getJsonChanges: (state, getters) => {
        const doneJson = state.helper.getChangesJsonFromChangeSets(builderModel, getters.unpersistedDoneChangeSets, true);
        const undoneJson = state.helper.getChangesJsonFromChangeSets(builderModel, getters.unpersistedUndoneChangeSets, false);
        return state.helper.mergeJson(doneJson, undoneJson);
      }
    }
  };
}
